import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

const RELOAD_KEY = 'bh_admin_chunk_reload';

export function isChunkLoadError(error: unknown): boolean {
  if (!error) return false;
  const err = error as { name?: string; message?: string };
  const msg = String(err.message || '');
  return (
    err.name === 'ChunkLoadError' ||
    /loading chunk/i.test(msg) ||
    /failed to fetch dynamically imported module/i.test(msg) ||
    /importing a module script failed/i.test(msg)
  );
}

/**
 * React.lazy wrapper that retries once after a short delay, then forces a
 * full reload when webpack chunk hashes go stale (dev HMR / new deploys).
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>
): LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      const mod = await factory();
      try {
        sessionStorage.removeItem(RELOAD_KEY);
      } catch {
        /* ignore */
      }
      return mod;
    } catch (error) {
      if (!isChunkLoadError(error)) {
        throw error;
      }

      await new Promise((r) => setTimeout(r, 400));

      try {
        return await factory();
      } catch (retryError) {
        let alreadyReloaded = false;
        try {
          alreadyReloaded = sessionStorage.getItem(RELOAD_KEY) === '1';
        } catch {
          /* ignore */
        }

        if (!alreadyReloaded && typeof window !== 'undefined') {
          try {
            sessionStorage.setItem(RELOAD_KEY, '1');
          } catch {
            /* ignore */
          }
          window.location.reload();
          // Keep Suspense pending until reload completes
          return new Promise(() => undefined) as Promise<{ default: T }>;
        }

        throw retryError;
      }
    }
  });
}
